import { useCallback, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Combobox, Input, useCombobox } from '@mantine/core';
import clsx from 'clsx';

import { FormLabel } from '../formlabel/FormLabel';

import { PeriodPickerDropdown } from './PeriodPickerDropdown';
import {
  comparePeriods,
  getCurrentPeriod,
  getPeriodEndIso,
  getPeriodStartIso,
  isoToPeriod,
  PeriodType,
  PeriodValue,
} from './periodRange';
import { PeriodRangeInput } from './PeriodRangeInput';
import { SharedPeriodRangePickerProps } from './PeriodRangePicker';

import datePickerStyles from '../datepicker/date-picker.module.scss';
import inputStyles from '../input/input.module.scss';

type PeriodBoundary = 'start' | 'end';

export type SharedPeriodPickerProps = Omit<
  SharedPeriodRangePickerProps,
  | 'value'
  | 'defaultValue'
  | 'onChange'
  | 'onCommit'
  | 'presets'
  | 'placeholderFrom'
  | 'placeholderTo'
> & {
  boundary?: PeriodBoundary;
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  onChange?: (value: string | undefined) => void;
  onCommit?: (value: string | undefined) => void;
};

type PeriodPickerProps = SharedPeriodPickerProps & {
  type: PeriodType;
  periodsPerYear: number;
  gridColumns?: number;
  format: (value: PeriodValue) => string;
  parse: (input: string) => PeriodValue | undefined;
  formatGridPeriod: (period: number) => string;
};

export function PeriodPicker(props: PeriodPickerProps) {
  const {
    type,
    periodsPerYear,
    gridColumns = periodsPerYear,
    format,
    parse,
    formatGridPeriod,
    label,
    description,
    required,
    error,
    value,
    defaultValue,
    onChange,
    onCommit,
    boundary = 'end',
    minDate,
    maxDate,
    placeholder,
    pageSize = 4,
    disabled = false,
    loading = false,
    readOnly = false,
    clearable = true,
    withinPortal = true,
    icon = 'event',
    className,
    'data-testid': testId = `${type}-picker`,
  } = props;
  const { t } = useTranslation();
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  // `undefined` is a real cleared value, so presence of the prop decides control.
  const isControlled = 'value' in props;
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const selected = isControlled ? value : uncontrolledValue;
  const [inputResetVersion, setInputResetVersion] = useState(0);
  const currentPeriod = useMemo(() => getCurrentPeriod(type), [type]);
  const yearsBeforeAnchor = Math.floor((pageSize - 1) / 2);
  const [pageStart, setPageStart] = useState(
    currentPeriod.year - yearsBeforeAnchor
  );
  const valueRef = useRef(selected);
  valueRef.current = selected;
  const lastCommitted = useRef(selected);

  const setSelected = useCallback(
    (next: string | undefined) => {
      if (valueRef.current === next) {
        return;
      }
      valueRef.current = next;
      if (!isControlled) {
        setUncontrolledValue(next);
      }
      onChange?.(next);
    },
    [isControlled, onChange]
  );

  const commitValue = useCallback(
    (next = valueRef.current) => {
      if (lastCommitted.current !== next) {
        lastCommitted.current = next;
        onCommit?.(next);
      }
    },
    [onCommit]
  );

  const combobox = useCombobox({
    onDropdownClose: () => {
      commitValue();
      inputRef.current?.blur();
    },
  });

  const toIso = useCallback(
    (period: PeriodValue) =>
      boundary === 'start'
        ? getPeriodStartIso(period, type)
        : getPeriodEndIso(period, type),
    [boundary, type]
  );

  const isWithinBounds = useCallback(
    (period: PeriodValue) => {
      const iso = toIso(period);
      return (!minDate || iso >= minDate) && (!maxDate || iso <= maxDate);
    },
    [maxDate, minDate, toIso]
  );

  const applyPeriod = useCallback(
    (period: PeriodValue) => {
      if (!isWithinBounds(period)) {
        return;
      }
      setSelected(toIso(period));
    },
    [isWithinBounds, setSelected, toIso]
  );

  const isUnavailable = disabled || loading || readOnly;

  function openPicker() {
    if (isUnavailable) {
      return;
    }
    const startsInteraction = !combobox.dropdownOpened;
    if (startsInteraction) {
      lastCommitted.current = valueRef.current;
    }
    const selectedPeriod = isoToPeriod(valueRef.current, type);
    if (selectedPeriod || startsInteraction) {
      setPageStart((selectedPeriod ?? currentPeriod).year - yearsBeforeAnchor);
    }
    combobox.openDropdown();
  }

  function clearValue() {
    setSelected(undefined);
    lastCommitted.current = undefined;
    onCommit?.(undefined);
  }

  function selectPeriod(period: PeriodValue) {
    if (!isWithinBounds(period)) {
      return;
    }
    setInputResetVersion((version) => version + 1);
    setSelected(toIso(period));
    combobox.closeDropdown();
  }

  function commitAndClose() {
    commitValue();
    setTimeout(() => combobox.closeDropdown());
  }

  const years = Array.from(
    { length: pageSize },
    (_, index) => pageStart + index
  );
  const selectedPeriod = isoToPeriod(selected, type);
  const valueIsInvalid = Boolean(
    selected &&
    ((minDate && selected < minDate) || (maxDate && selected > maxDate))
  );

  return (
    <div
      className={clsx(datePickerStyles['root'], className)}
      data-testid={testId}
    >
      <FormLabel
        id={id}
        label={label}
        description={description}
        required={required}
        disabled={disabled}
        loading={loading}
        error={error}
      />
      <Combobox
        unstyled
        disabled={isUnavailable}
        store={combobox}
        withinPortal={withinPortal}
        classNames={{ dropdown: datePickerStyles['dropdown'] }}
      >
        <Combobox.EventsTarget>
          <div
            className={datePickerStyles['dates-wrapper']}
            onBlur={() =>
              setTimeout(() => {
                if (document.activeElement !== inputRef.current) {
                  combobox.closeDropdown();
                }
              })
            }
          >
            <Combobox.DropdownTarget>
              <PeriodRangeInput
                id={id}
                data-testid={`${type}-picker-input`}
                ref={inputRef}
                value={selectedPeriod}
                format={format}
                parse={parse}
                validate={isWithinBounds}
                focused={combobox.dropdownOpened}
                disabled={disabled}
                loading={loading}
                readOnly={readOnly}
                clearable={clearable}
                fullWidth
                icon={icon}
                placeholder={
                  placeholder ??
                  (type === 'month'
                    ? t('app.periodPicker.month')
                    : t('app.periodPicker.quarter'))
                }
                externalError={Boolean(error) || valueIsInvalid}
                resetVersion={inputResetVersion}
                onClear={clearValue}
                onFocus={openPicker}
                onValidChange={applyPeriod}
                onValidCommit={commitAndClose}
              />
            </Combobox.DropdownTarget>
          </div>
        </Combobox.EventsTarget>

        <PeriodPickerDropdown
          years={years}
          periodsPerYear={periodsPerYear}
          gridColumns={gridColumns}
          currentPeriod={currentPeriod}
          formatGridPeriod={formatGridPeriod}
          arrowPosition="start"
          isUnavailable={(period) => !isWithinBounds(period)}
          isSelected={(period) =>
            selectedPeriod != null &&
            comparePeriods(period, selectedPeriod) === 0
          }
          onPageEarlier={() => setPageStart((year) => year - pageSize)}
          onPageLater={() => setPageStart((year) => year + pageSize)}
          onSelectPeriod={selectPeriod}
        />
      </Combobox>
      {typeof error === 'string' && error && (
        <Input.Error className={inputStyles['input-error']}>
          {error}
        </Input.Error>
      )}
    </div>
  );
}
